import { CurrencyPipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ApexOptions, NgApexchartsModule } from 'ng-apexcharts';
import { VehicleStockData, VehicleStockDto } from '../mandat-dashboard/mandat-dashboard.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

export type RangeKey = '30 days' | '3 months' | '6 months' | '1 year';

@Component({
  selector: 'app-vehicle-stock',
  templateUrl: './vehicle-stock.component.html',
  styleUrls: ['./vehicle-stock.component.css'],
  imports: [
    NgApexchartsModule,
    MatButtonToggleModule,
    CurrencyPipe,
    TranslocoPipe
  ]
})
export class VehicleStockComponent implements OnInit, OnChanges {
  chartVehicleStock: ApexOptions = {};

  @Input() data: VehicleStockData = {
    arrived: [],
    sold: [],
    currentlyInStock: 0,
    inStockTotalValue: 0,
    soldVehicles: 0,
    reserved: 0,
    upcoming: 0,
    purchased: 0,
    tradeIn: 0
  };

  @Input() range = signal<RangeKey>('30 days');

  private labels: string[] = [];

  /**
   *
   */
  constructor(private _transLocoService: TranslocoService) {
    
  }

  ngOnInit() {
    this._prepareChartData();
  }

  ngOnChanges(_: SimpleChanges) {
    // Recompute when fresh data arrives
    this._prepareChartData();
  }

  onRangeChange(value: RangeKey) {
    this.range.set(value);
  }

  private _prepareChartData(): void {
    const latest = this._latestDateAcross(this.data.arrived, this.data.sold) ?? new Date();

    // Compute series + labels based on selectedRange
    let arrivedSeries: number[] = [];
    let soldSeries: number[] = [];

    if (this.range() === '30 days') {
      // Weekly buckets (5 recent ISO weeks)
      this.labels = this._lastNWeekLabels(latest, 5);
      const weekKeys = this._labelsToIsoWeekKeys(this.labels);
      arrivedSeries = this._groupByWeek(this.data.arrived, weekKeys);
      soldSeries = this._groupByWeek(this.data.sold, weekKeys);
    } else {
      // Monthly buckets
      const monthsCount =
        this.range() === '3 months' ? 3 :
          this.range() === '6 months' ? 6 : 12;

      const { labels, monthKeys } = this._lastNMonthLabelsAndKeys(latest, monthsCount);
      this.labels = labels;
      arrivedSeries = this._groupByMonth(this.data.arrived, monthKeys);
      soldSeries = this._groupByMonth(this.data.sold, monthKeys);
    }

    this.chartVehicleStock = {
      chart: {
        fontFamily: 'inherit',
        foreColor: 'inherit',
        height: '100%',
        type: 'line',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: ['#64748B', '#94A3B8'],
      dataLabels: {
        enabled: true,
        enabledOnSeries: [0],
        background: { borderWidth: 0 },
      },
      grid: { borderColor: 'var(--fuse-border)' },
      labels: this.labels,
      legend: { show: false },
      plotOptions: { bar: { columnWidth: '50%' } },
      series: [
        { name: this._transLocoService.translate('Arrived'), type: 'line', data: arrivedSeries },
        { name: this._transLocoService.translate('Sold'), type: 'column', data: soldSeries }
      ],
      states: { hover: { filter: { type: 'darken' } } },
      stroke: { width: [3, 0] },
      tooltip: { followCursor: true, theme: 'dark' },
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { color: 'var(--fuse-border)' },
        labels: { style: { colors: 'var(--fuse-text-secondary)' } },
        tooltip: { enabled: false },
      },
      yaxis: {
        labels: {
          offsetX: -16,
          style: { colors: 'var(--fuse-text-secondary)' },
        },
      },
    };
  }

  /** Helpers **/

  private _latestDateAcross(a: VehicleStockDto[], b: VehicleStockDto[]): Date | null {
    const all = [...(a || []), ...(b || [])];
    if (!all.length) return null;
    return all.map(x => new Date(x.date))
      .reduce((max, d) => (d > max ? d : max), new Date(0));
  }

  /** WEEK LABELS + GROUPING (Last 30 days view) **/
  private _lastNWeekLabels(latestDate: Date, n: number): string[] {
    const labels: string[] = [];
    // Build ISO week labels like “W33”, “W34”, … with year if it rolls over
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(latestDate);
      d.setUTCDate(d.getUTCDate() - i * 7);
      const { year, week } = this._isoYearWeek(d);
      labels.push(`${year}-W${week}`);
    }
    return labels;
  }

  private _labelsToIsoWeekKeys(labels: string[]): string[] {
    // Same strings (YYYY-W##) are used as keys
    return labels;
  }

  private _groupByWeek(vehicles: VehicleStockDto[], weekKeys: string[]): number[] {
    if (!vehicles?.length) return weekKeys.map(() => 0);

    const counts: Record<string, number> = {};
    for (const v of vehicles) {
      const { year, week } = this._isoYearWeek(new Date(v.date));
      const key = `${year}-W${week}`;
      counts[key] = (counts[key] || 0) + 1;
    }
    return weekKeys.map(k => counts[k] || 0);
  }

  private _isoYearWeek(date: Date): { year: number; week: number } {
    const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum); // Thursday
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
    return { year: d.getUTCFullYear(), week };
  }

  /** MONTH LABELS + GROUPING (3/6/12 months views) **/
  private _lastNMonthLabelsAndKeys(latest: Date, n: number): { labels: string[]; monthKeys: string[] } {
    const labels: string[] = [];
    const keys: string[] = []; // YYYY-MM

    // Snap to first of month
    const end = new Date(Date.UTC(latest.getUTCFullYear(), latest.getUTCMonth(), 1));
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() - i, 1));
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      keys.push(key);
      labels.push(this._monthLabel(d)); // e.g., "Aug 2025"
    }
    return { labels, monthKeys: keys };
  }

  private _monthLabel(d: Date): string {
    // “Jan 2025” style
    const monthShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()];
    return `${monthShort} ${d.getUTCFullYear()}`;
  }

  private _groupByMonth(vehicles: VehicleStockDto[], monthKeys: string[]): number[] {
    if (!vehicles?.length) return monthKeys.map(() => 0);

    const counts: Record<string, number> = {};
    for (const v of vehicles) {
      const dv = new Date(v.date);
      const key = `${dv.getUTCFullYear()}-${String(dv.getUTCMonth() + 1).padStart(2, '0')}`;
      counts[key] = (counts[key] || 0) + 1;
    }
    return monthKeys.map(k => counts[k] || 0);
  }
}
